<template>
  <Teleport to="body">
    <transition name="ic-toast">
      <div
        v-if="visible"
        class="ic-toast"
        role="alertdialog"
        aria-labelledby="ic-title"
        aria-describedby="ic-phone"
      >
        <div class="ic-glow" aria-hidden="true"></div>

        <header class="ic-head">
          <div class="ic-avatar">{{ initials }}</div>
          <div class="ic-id">
            <div id="ic-title" class="ic-name">{{ displayName }}</div>
            <div id="ic-phone" class="ic-phone">
              <Icon name="phone-incoming" :size="11" />
              <span class="mono">{{ phone }}</span>
            </div>
          </div>
          <div class="ic-pulse" aria-hidden="true">
            <span></span><span></span><span></span>
          </div>
        </header>

        <div class="ic-info">
          <!-- Adresse réservée aux superviseurs+. Les agents voient le nom,
               le service précédent et l'historique, mais pas le lead complet. -->
          <div v-if="!restricted && (city || address)" class="ic-row">
            <Icon name="map-pin" :size="12" />
            <span class="truncate">{{ address || city }}</span>
          </div>
          <div v-if="serviceRequested" class="ic-row highlight">
            <Icon name="briefcase" :size="12" />
            <span>Dernier besoin : <b>{{ serviceRequested }}</b></span>
          </div>
          <div v-if="ticketCount > 0" class="ic-row">
            <Icon name="file-text" :size="12" />
            <span>{{ ticketCount }} fiche(s) historique</span>
          </div>
          <div v-if="!hasInfo" class="ic-row muted">
            <Icon name="info" :size="12" />
            <span>Numéro inconnu</span>
          </div>
        </div>

        <div class="ic-actions">
          <button class="ic-decline" type="button" @click="$emit('decline')" aria-label="Refuser l'appel (Escape)">
            <Icon name="phone-off" :size="16" />
            <span>Refuser</span>
            <kbd class="ic-kbd">Esc</kbd>
          </button>
          <button class="ic-answer" type="button" @click="$emit('answer')" aria-label="Décrocher (Espace)">
            <Icon name="phone" :size="16" />
            <span>Décrocher</span>
            <kbd class="ic-kbd">Espace</kbd>
          </button>
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import Icon from '@/shared/components/Icon.vue'
import type { ContactLookup } from '@/shared/api/queue'

const props = withDefaults(
  defineProps<{
    visible: boolean
    phone: string
    lookup: ContactLookup | null
    fallbackName?: string
    /** Si vrai, masque l'adresse (réservée aux superviseurs+). */
    restricted?: boolean
  }>(),
  { fallbackName: '', restricted: false },
)

defineEmits<{
  (e: 'answer'): void
  (e: 'decline'): void
}>()

const queueEntry = computed(() => props.lookup?.queue_entry ?? null)
const ticketCount = computed(() => props.lookup?.ticket_count ?? 0)
const displayName = computed(
  () => queueEntry.value?.name || props.fallbackName || 'Appel entrant',
)
const initials = computed(() => {
  const n = displayName.value
  if (!n || n === 'Appel entrant') return '?'
  return n.split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() || '').join('')
})
const customData = computed(() => queueEntry.value?.custom_data || {})
const city = computed<string>(() => customData.value.city || '')
const address = computed<string>(() => customData.value.address || '')
const serviceRequested = computed<string>(() => queueEntry.value?.service_requested || '')
const hasInfo = computed(
  () => !!(queueEntry.value || city.value || address.value || ticketCount.value),
)
</script>

<style scoped>
.ic-toast {
  position: fixed;
  top: 70px;
  right: 20px;
  z-index: 9999;
  width: 360px;
  max-width: calc(100vw - 40px);
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-xl), 0 0 0 4px rgba(99, 102, 241, 0.10);
  padding: 18px;
  overflow: hidden;
}

.ic-glow {
  position: absolute;
  inset: -50% -20% auto auto;
  width: 200px;
  height: 200px;
  background: radial-gradient(circle, rgba(99,102,241,0.18) 0%, transparent 70%);
  pointer-events: none;
  animation: glow-pulse 1.6s ease-in-out infinite;
}
@keyframes glow-pulse {
  0%, 100% { transform: scale(0.95); opacity: 0.6; }
  50% { transform: scale(1.05); opacity: 1; }
}

.ic-head {
  display: flex; align-items: center; gap: 12px;
  position: relative;
  z-index: 1;
}
.ic-avatar {
  width: 44px; height: 44px;
  border-radius: var(--radius-full);
  background: linear-gradient(135deg, var(--primary) 0%, var(--primary-pressed) 100%);
  color: white;
  display: flex; align-items: center; justify-content: center;
  font-size: 15px; font-weight: 700;
  flex-shrink: 0;
  box-shadow: 0 2px 8px rgba(99,102,241,0.30);
}
.ic-id { flex: 1; min-width: 0; }
.ic-name {
  font-size: 15px; font-weight: 600;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  letter-spacing: -0.01em;
}
.ic-phone {
  display: flex; align-items: center; gap: 5px;
  margin-top: 2px;
  font-size: 12.5px; color: var(--text-muted);
}

/* Animated "ringing" dots */
.ic-pulse {
  display: flex; align-items: end; gap: 3px;
  height: 22px;
  flex-shrink: 0;
}
.ic-pulse span {
  display: block;
  width: 3px;
  background: var(--primary);
  border-radius: 2px;
  animation: ring-bar 1s ease-in-out infinite;
}
.ic-pulse span:nth-child(1) { height: 50%; animation-delay: 0s; }
.ic-pulse span:nth-child(2) { height: 100%; animation-delay: 0.15s; }
.ic-pulse span:nth-child(3) { height: 70%; animation-delay: 0.3s; }
@keyframes ring-bar {
  0%, 100% { transform: scaleY(0.4); }
  50% { transform: scaleY(1); }
}

.ic-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 12px 0;
  padding: 10px 12px;
  background: var(--bg);
  border-radius: var(--radius);
  font-size: 12.5px;
  position: relative;
  z-index: 1;
}
.ic-row {
  display: flex; align-items: center; gap: 6px;
  color: var(--text-secondary);
}
.ic-row.muted { color: var(--text-muted); font-style: italic; }
.ic-row.highlight { color: var(--info); font-weight: 500; }
.ic-row .truncate {
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.ic-actions {
  display: grid;
  grid-template-columns: 1fr 1.4fr;
  gap: 8px;
  position: relative;
  z-index: 1;
}
.ic-answer, .ic-decline {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 11px 14px;
  border: none;
  border-radius: var(--radius);
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  transition: transform var(--duration-fast), filter var(--duration-fast);
}
.ic-decline {
  background: var(--surface-hover);
  color: var(--danger);
  border: 1px solid var(--danger-border);
}
.ic-decline:hover { background: var(--danger-bg); }
.ic-answer {
  background: var(--success);
  color: white;
  box-shadow: 0 2px 8px rgba(22,163,74,0.30);
}
.ic-answer:hover { filter: brightness(1.05); transform: translateY(-1px); }
.ic-answer:focus-visible, .ic-decline:focus-visible {
  outline: 2px solid var(--primary); outline-offset: 2px;
}

.ic-kbd {
  font-family: var(--font-mono);
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 3px;
  background: rgba(255,255,255,0.18);
  border: 1px solid rgba(255,255,255,0.25);
  letter-spacing: 0;
  margin-left: 2px;
}
.ic-decline .ic-kbd {
  background: rgba(220,38,38,0.10);
  border-color: rgba(220,38,38,0.20);
  color: var(--danger);
}

/* Enter/leave */
.ic-toast-enter-from, .ic-toast-leave-to {
  opacity: 0;
  transform: translateY(-12px) scale(0.96);
}
.ic-toast-enter-active, .ic-toast-leave-active {
  transition: opacity 0.18s var(--ease-out), transform 0.18s var(--ease-out);
}

.mono { font-family: var(--font-mono); }
</style>
